/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { Program } from '@/api/models';
import { parseDbTimestamp } from './dbDate';

/**
 * The build releases a vehicle program passes through, earliest first.
 *
 * `PSORP` and `RC` name the same final stage and are written either way, so both spell it.
 */
const BUILD_RELEASE_SEQUENCE: readonly (readonly string[])[] = [
  ['PPTB'],
  ['PVVB'],
  ['APPV'],
  ['PPV'],
  ['MVBNS'],
  ['MVBS'],
  ['SORP'],
  ['PSORP', 'RC'],
];

const RANKS = new Map<string, number>(
  BUILD_RELEASE_SEQUENCE.flatMap((codes, stage) => codes.map((code) => [code, stage] as const)),
);

/** Sorts below every release we recognize, so an unknown code never claims to be the latest. */
const UNRANKED = -1;

/**
 * How far through the build sequence a release is, or `null` for a code outside it.
 *
 * A value spelled "PSORP/RC" names one stage under both its names, so each part is tried.
 */
export function buildReleaseRank(release: string | null | undefined): number | null {
  const value = (release ?? '').trim().toUpperCase();
  for (const part of value.split('/')) {
    const rank = RANKS.get(part.trim());
    if (rank !== undefined) return rank;
  }
  return null;
}

/** Newest-first ordering instant; the dealer schedule is what dates these for a technician. */
function scheduledAt(program: Program): number {
  return (
    parseDbTimestamp(program.dealerScheduleDate) ?? parseDbTimestamp(program.dataEffectiveDate) ?? 0
  );
}

/**
 * Orders two data sets of one vehicle, newest first.
 *
 * The build release decides it, because the dates cannot: two releases of the same vehicle are
 * routinely published on one dealer schedule date -- the 2027 Silverado 1500 carries APPV and MVBS
 * both scheduled for the same day -- and ordering those on the date alone leaves the winner to
 * whichever row the catalog happened to return first. That is how the older release came to be
 * offered as "latest" and opened by default.
 *
 * Dates still separate releases at the same stage, and codes outside the sequence, where there is
 * nothing else to go on.
 */
export function compareDataSets(a: Program, b: Program): number {
  const rankA = buildReleaseRank(a.engineeringRelease) ?? UNRANKED;
  const rankB = buildReleaseRank(b.engineeringRelease) ?? UNRANKED;
  if (rankA !== rankB) return rankB - rankA;
  const atA = scheduledAt(a);
  const atB = scheduledAt(b);
  if (atA !== atB) return atB - atA;
  return b.id - a.id;
}

/** The distinct data sets among `programs`, newest build release first. */
export function orderDataSets(programs: Program[]): Program[] {
  const byId = new Map<number, Program>();
  for (const program of programs) {
    if (!byId.has(program.id)) byId.set(program.id, program);
  }
  return [...byId.values()].sort(compareDataSets);
}
